/**
 * OptimizedImage component using Cloudinary for image optimization.
 *
 * Benefits:
 * - Automatic WebP/AVIF format conversion
 * - Responsive image resizing
 * - Quality optimization
 * - CDN caching (transformations are cached, not re-computed)
 * - Proper width/height to prevent layout shift (CLS)
 * - fetchpriority for above-fold images
 * - Lazy loading for below-fold images
 *
 * Requires VITE_CLOUDINARY_CLOUD_NAME environment variable.
 */

type ImageFit = 'cover' | 'contain'

interface OptimizedImageProps {
  /** The original image URL (e.g., Vercel Blob URL) */
  src: string
  /** Alt text for accessibility */
  alt: string
  /** Width of the image container */
  width: number
  /** Height of the image container */
  height: number
  /** Additional CSS classes */
  className?: string
  /** Whether this is an above-fold image that should load immediately */
  priority?: boolean
  /** How the image should fit its container */
  fit?: ImageFit
  /** Quality (1-100), defaults to 80 */
  quality?: number
  /** Responsive sizes attribute; defaults to the fixed width */
  sizes?: string
}

// Cloudinary cloud name from environment
const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME

/**
 * Generate Cloudinary fetch URL for an external image
 * Uses Cloudinary's "fetch" feature to optimize images from any URL
 */
function getCloudinaryUrl(src: string, width: number, height: number, fit: ImageFit, quality: number): string {
  if (!CLOUDINARY_CLOUD_NAME || !src.startsWith('http')) {
    // Fallback to original URL if Cloudinary is not configured or not an absolute URL
    return src
  }

  // Cloudinary crop modes: c_fill (cover), c_fit (contain)
  const cropMode = fit === 'cover' ? 'c_fill' : 'c_fit'

  // Build transformation string
  // f_auto: automatic format (WebP/AVIF based on browser)
  // q_auto or q_<number>: quality
  // w_<width>,h_<height>: dimensions
  const transforms = [`f_auto`, `q_${quality}`, `w_${width}`, `h_${height}`, cropMode].join(',')

  // Cloudinary fetch URL format
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/fetch/${transforms}/${encodeURIComponent(src)}`
}

/**
 * Generate srcset for responsive images
 */
function generateSrcSet(src: string, baseWidth: number, height: number, fit: ImageFit, quality: number): string {
  if (!CLOUDINARY_CLOUD_NAME || !src.startsWith('http')) return ''

  // Generate 1x and 2x versions for retina displays
  const widths = [baseWidth, baseWidth * 2]
  return widths.map(w => `${getCloudinaryUrl(src, w, Math.round((height / baseWidth) * w), fit, quality)} ${w}w`).join(', ')
}

export function OptimizedImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  fit = 'cover',
  quality = 80,
  sizes
}: OptimizedImageProps) {
  const optimizedSrc = getCloudinaryUrl(src, width, height, fit, quality)
  const srcSet = generateSrcSet(src, width, height, fit, quality)

  return (
    <img
      src={optimizedSrc}
      srcSet={srcSet || undefined}
      sizes={sizes ?? `${width}px`}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
      style={{
        objectFit: fit
      }}
    />
  )
}

/**
 * Simplified props for common use cases
 */
interface ProductImageProps {
  src: string
  alt: string
  className?: string
  priority?: boolean
}

/**
 * Pre-configured component for product thumbnails (e.g., in grid views)
 * Size: 256x256
 */
export function ProductThumbnail({ src, alt, className = '', priority = false }: ProductImageProps) {
  return <OptimizedImage src={src} alt={alt} width={256} height={256} className={className} priority={priority} fit="contain" />
}

/**
 * Pre-configured component for main product images (e.g., on product detail page)
 * Size: 512x512
 */
export function ProductMainImage({ src, alt, className = '', priority = true }: ProductImageProps) {
  return <OptimizedImage src={src} alt={alt} width={512} height={512} className={className} priority={priority} fit="contain" />
}

/**
 * Pre-configured component for small sidebar thumbnails
 * Size: 128x128
 */
export function ProductSidebarThumbnail({ src, alt, className = '', priority = false }: ProductImageProps) {
  return <OptimizedImage src={src} alt={alt} width={128} height={128} className={className} priority={priority} fit="cover" />
}
